import type {
  AuthSession,
  AutoReplyRule,
  AutoReplyTestResult,
  Conversation,
  DeliverySettings,
  DualhookSettings,
  FreeScoutSettings,
  Message,
  Page,
  SystemStatus,
  SystemEvent,
  TemplateProfile,
} from '../types'

let csrfToken = ''

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const setCsrfToken = (value?: string): void => {
  csrfToken = value ?? ''
}

const apiFetch = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  if (init.method && !['GET', 'HEAD'].includes(init.method.toUpperCase()) && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken)
  }
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' })
  const contentType = response.headers.get('content-type') ?? ''
  const body = contentType.includes('application/json')
    ? await response.json()
    : await response.text()
  if (!response.ok) {
    const payload = typeof body === 'object' && body ? (body as Record<string, unknown>) : {}
    throw new ApiError(
      typeof payload.error === 'string' ? payload.error : `Richiesta fallita (${response.status})`,
      response.status,
      typeof payload.code === 'string' ? payload.code : undefined,
    )
  }
  return body as T
}

export const api = {
  session: () => apiFetch<AuthSession>('/api/auth/session'),
  login: (username: string, password: string) =>
    apiFetch<AuthSession>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () => apiFetch<{ success: boolean }>('/api/auth/logout', { method: 'POST' }),
  conversations: (options: { cursor?: string; query?: string; unreadOnly?: boolean }) => {
    const params = new URLSearchParams({ limit: '40' })
    if (options.cursor) params.set('cursor', options.cursor)
    if (options.query) params.set('q', options.query)
    if (options.unreadOnly) params.set('unread', 'true')
    return apiFetch<Page<Conversation>>(`/api/conversations?${params}`)
  },
  conversation: (id: string) => apiFetch<Conversation>(`/api/conversations/${id}`),
  messages: (conversationId: string, before?: string) => {
    const params = new URLSearchParams({ limit: '50' })
    if (before) params.set('before', before)
    return apiFetch<Page<Message>>(`/api/conversations/${conversationId}/messages?${params}`)
  },
  sendText: (conversationId: string, text: string) =>
    apiFetch<Message>(`/api/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  sendAttachment: (conversationId: string, file: File, caption?: string) => {
    const body = new FormData()
    body.set('file', file)
    if (caption) body.set('caption', caption)
    return apiFetch<Message>(`/api/conversations/${conversationId}/attachments`, {
      method: 'POST',
      body,
    })
  },
  markRead: (conversationId: string) =>
    apiFetch<{ success: boolean }>(`/api/conversations/${conversationId}/read`, {
      method: 'POST',
    }),
  retryMessage: (messageId: string) =>
    apiFetch<Message>(`/api/messages/${messageId}/retry`, { method: 'POST' }),
  dualhookSettings: () => apiFetch<DualhookSettings>('/api/settings/dualhook'),
  generateWebhookTokens: () =>
    apiFetch<{ webhookVerifyToken: string; webhookPathSecret: string }>(
      '/api/settings/dualhook/generate-tokens',
      { method: 'POST' },
    ),
  saveDualhookSettings: (settings: Record<string, unknown>) =>
    apiFetch<DualhookSettings>('/api/settings/dualhook', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  testDualhook: () =>
    apiFetch<DualhookSettings['connection']>('/api/settings/dualhook/test', { method: 'POST' }),
  freeScoutSettings: () => apiFetch<FreeScoutSettings>('/api/settings/freescout'),
  saveFreeScoutSettings: (settings: Record<string, unknown>) =>
    apiFetch<FreeScoutSettings>('/api/settings/freescout', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  testFreeScout: () =>
    apiFetch<FreeScoutSettings['connection']>('/api/settings/freescout/test', {
      method: 'POST',
    }),
  systemStatus: () => apiFetch<SystemStatus>('/api/system/status'),
  systemErrors: () => apiFetch<{ items: SystemEvent[] }>('/api/system/errors'),
  freeScoutFailedJobs: () =>
    apiFetch<{
      items: Array<{ id: string; attempts: number; errorMessage?: string; createdAt: string }>
    }>('/api/system/freescout/jobs'),
  retryFreeScoutJob: (id: string) =>
    apiFetch<{ success: boolean }>(`/api/system/freescout/jobs/${id}/retry`, { method: 'POST' }),
  deliverySettings: () => apiFetch<DeliverySettings>('/api/settings/delivery'),
  setDeliveryEnabled: (enabled: boolean) =>
    apiFetch<DeliverySettings>('/api/settings/delivery/enabled', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),
  createDeliveryCredential: (label: string) =>
    apiFetch<{
      credential: DeliverySettings['credentials'][number]
      token: string
    }>('/api/settings/delivery/credentials', {
      method: 'POST',
      body: JSON.stringify({ label }),
    }),
  revokeDeliveryCredential: (id: string) =>
    apiFetch<{ success: boolean }>(`/api/settings/delivery/credentials/${id}/revoke`, {
      method: 'POST',
    }),
  saveTemplateProfile: (profile: TemplateProfile) =>
    apiFetch<TemplateProfile>(`/api/settings/delivery/profiles/${profile.key}`, {
      method: 'PUT',
      body: JSON.stringify({
        key: profile.key,
        label: profile.label,
        deliveryType: profile.deliveryType,
        templateName: profile.templateName,
        languageCode: profile.languageCode,
        variableNames: profile.variableNames,
        previewText: profile.previewText,
        workflowCode: profile.workflowCode,
        buttonMappings: profile.buttonMappings,
        confirmationText: profile.confirmationText,
        bookingUrl: profile.bookingUrl,
        headerMediaType: profile.headerMediaType,
        headerMediaUrl: profile.headerMediaUrl,
        active: profile.active,
      }),
    }),
  saveTemplateConfirmationText: (key: string, confirmationText: string) =>
    apiFetch<TemplateProfile>(`/api/settings/delivery/profiles/${key}/confirmation-text`, {
      method: 'PATCH',
      body: JSON.stringify({ confirmationText }),
    }),
  verifyTemplateProfile: (key: string) =>
    apiFetch<TemplateProfile>(`/api/settings/delivery/profiles/${key}/verify`, {
      method: 'POST',
    }),
  autoReplySettings: () => apiFetch<{ rules: AutoReplyRule[] }>('/api/settings/auto-replies'),
  saveAutoReplyRule: (rule: AutoReplyRule) =>
    apiFetch<AutoReplyRule>(`/api/settings/auto-replies/${rule.key}`, {
      method: 'PUT',
      body: JSON.stringify({
        key: rule.key,
        label: rule.label,
        identifier: rule.identifier,
        exactPhrase: rule.exactPhrase,
        responseText: rule.responseText,
        enabled: rule.enabled,
        priority: rule.priority,
        cooldownMinutes: rule.cooldownMinutes,
      }),
    }),
  testAutoReply: (text: string) =>
    apiFetch<AutoReplyTestResult>('/api/settings/auto-replies/test', {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
}
